import { Typography } from "@mui/material";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "components/icon/mui_icon";
import MuiSelect from "./MuiSelect";
import MuiMenuItem from "./MuiMenuItem";
import { StyledInputField, StyledTextField } from "customStyled";
import { withTrimHandler } from "utils/reactiveFormFunc";
import FormControlUI from "../FormControlUI";
import { forwardRef, useMemo, useRef } from "react";
import { autocompleteToken } from "utils";
import { placeholderOption, placeholderText, selectText } from "./selectPlaceholder";

const SelectInputUI = forwardRef((props, ref) => {
  const {
    id,
    autoComplete,
    placeholder = null,
    optionList,
    sx = {},
    multiple = false,
    variant = "outlined",
    label = null,
    required,
    helperText,
    onChange,
    value,
    disabled = false,
    ariaLabel,
  } = props;
  const selectRef = useRef(null);

  // useImperativeHandle(ref, () => ({
  //   blur: () => {
  //     innerRef.current?.blur?.();
  //   },
  //   focus: () => {
  //     innerRef.current?.focus?.();
  //   },
  // }));
  const placeHolder = placeholder ? selectText(placeholder) : null;
  const optionLists = useMemo(
    () => (Array.isArray(optionList) ? optionList : []),
    [optionList],
  );

  // ✅ 1. Create map for fast lookup
  const valueLabelMap = useMemo(() => {
    const map = new Map();
    optionLists.forEach((opt) => {
      map.set(opt.value, opt.label);
    });
    return map;
  }, [optionLists]);

  // ✅ 2. Centralized placeholder
  const renderPlaceholder = () => (
    <Typography sx={(theme) => ({ color: theme.palette.grey[500] })}>
      {placeHolder ?? selectText()}
    </Typography>
  );

  // ✅ 3. Clean renderValue
  const renderValue = (selected) => {
    // 🔑 Always check map FIRST
    if (!multiple) {
      return valueLabelMap.get(selected) ?? renderPlaceholder();
    }

    // multiple
    if (!Array.isArray(selected) || selected.length === 0) {
      return renderPlaceholder();
    }

    const labels = selected
      .map((val) => valueLabelMap.get(val))
      .filter(Boolean);

    return labels.length ? labels.join(", ") : renderPlaceholder();
  };

  // A saved value whose option has not loaded yet (e.g. the campus while the
  // campus list loads) shows the placeholder; MUI warns about a value that
  // matches no option, so the select gets "" until the option is there
  const shownValue =
    !multiple && value !== "" && value != null && !valueLabelMap.has(value) ? "" : value;

  return (
    <FormControlUI
      label={label}
      required={required}
      id={id}
      sx={sx}
      helperText={helperText}
      disabled={disabled}
    >
      <MuiSelect
        id={id}
        name={id}
        ref={selectRef}
        // named by its label; without one, by ariaLabel or the placeholder
        labelId={label ? `${id}-label` : undefined}
        inputProps={label ? undefined : { "aria-label": ariaLabel || placeholder || id }}
        IconComponent={KeyboardArrowDownIcon}
        autoComplete={autocompleteToken(autoComplete)}
        {...withTrimHandler(() => ({ onChange, value: shownValue }))}
        displayEmpty
        variant={variant}
        input={
          variant === "outlined" ? <StyledTextField /> : <StyledInputField />
        }
        multiple={multiple}
        disabled={disabled}
        renderValue={renderValue}
        aria-describedby={`${id}-helper-text`}
        autoWidth
      >
        {/* the first, disabled option: the placeholder, or "no options" (not when
            an option already stands for "nothing chosen", e.g. "All Users") */}
        {optionLists.some((o) => o.value === "")
          ? null
          : placeholderOption(placeholderText({ placeholder: placeHolder, hasOptions: optionLists.length > 0 }))}
        {optionLists.map((option, index) => (
          <MuiMenuItem key={`${option.value}-${index}`} value={option.value}>
            {option.label}
          </MuiMenuItem>
        ))}
      </MuiSelect>
    </FormControlUI>
  );
});

export default SelectInputUI;
